<template>
  <div class="collection-sort-and-filter" :class="{ 'mb-sm': isSibSearch }">
    <template v-if="newFilterVersion">
      <div class="collection-sort-and-filter--new">
        <sib-tag
          :role="sumNumberFilterSelected ? 'primary' : 'default'"
          @click="showFilterPopup()"
          class="tag-all-filter"
        >
          <IconFilterSvg
            class="mr-xs"
            :class="{ 'filter-primary': sumNumberFilterSelected }"
          />Filter
          {{ sumNumberFilterSelected ? `(${sumNumberFilterSelected})` : '' }}
        </sib-tag>
        <HorizontalScroll
          class="ml-xs"
          :number-items="filterAttributes.length + 1"
          name="filter-short-menu"
          prefix-item-id="filter-menu-item"
        >
          <sib-tag
            class="tag-sort"
            :id="`filter-menu-item-0`"
            @click="handleOpenSortUsingUpDownArrow"
          >
            <IconSort class="mr-xs" />
            Sort
          </sib-tag>
          <sib-tag
            v-for="(filterAttribute, index) in filterAttributes"
            :key="index"
            :id="`filter-menu-item-${index + 1}`"
            :style="`overflow:unset`"
            class="ml-xs"
            :class="{
              'mr-xs': index == filterAttributes.length - 1,
            }"
            :role="
              getNumberFilterSelected(filterAttribute.attribute)
                ? 'primary'
                : 'default'
            "
            @click="showFilterPopup(filterAttribute.attribute)"
            primary
          >
            {{ filterAttribute.title }}
            {{
              getNumberFilterSelected(filterAttribute.attribute)
                ? `(${getNumberFilterSelected(filterAttribute.attribute)})`
                : ''
            }}
          </sib-tag>
        </HorizontalScroll>
      </div>
    </template>

    <template v-else>
      <!-- prettier-ignore-attribute -->
      <div
        class="option hidden-md-up sort-option"
        @click="handleOpenSortUsingUpDownArrow"
      >
        <UpDownArrow
          :type="showSort ? 'up' : 'down'"
          arrow-width="0.8em"
          class="mr-xs"
        />
        {{ sortOptions[selectedOptionIndex]?.value }}
      </div>
      <div class="hidden-md-down" @click="handleOpenSortUsingSelect">
        Sort:
        <sib-select
          :value="sortOptions[selectedOptionIndex]?.value || ''"
          :options="sortOptions"
          @input="onSortSelect"
          @focusout="isSelectOpen = false"
        ></sib-select>
      </div>
      <!-- prettier-ignore-attribute -->
      <div class="option filter-option" @click="showFilterPopup()">
        Filter
        <img :src="IconFilter" alt="filter" width="60" height="60" />
        <span class="filter-badge" v-show="hasFilter"></span>
      </div>
    </template>
    <client-only>
      <SortPopup
        :selected-option-index="selectedOptionIndex"
        :sort-options="sortOptions"
        :show="showSort"
        @input="onSortSelect"
        @update:show="showSort = $event"
        :dropdown="!newFilterVersion"
      ></SortPopup>
    </client-only>
    <client-only>
      <FilterPopup
        @apply-filter="hasFilter = $event"
        :filterSections="filterSections"
        :show="showFilter"
        @update:show="showFilter = $event"
        :pageType="pageType"
        :attribute-based-visibility="attributeBasedVisibility"
      ></FilterPopup>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import IconFilter from '@/assets/images/icons/icon-filter.png'
import IconSort from '@/assets/images/icons/icon-sort.svg'
import IconFilterSvg from '@/assets/images/icons/icon-filter.svg'
import UpDownArrow from '@/ui-kits/UpDownArrow.vue'
import SortPopup from './SortPopup.vue'
import FilterPopup from './FilterPopup.vue'
import HorizontalScroll from './HorizontalScroll.vue'
import SibTag from '@/ui-kits/Tag.vue'
import {
  COLLECTION_AFTER_QUERY_STRING,
  COLLECTION_BEFORE_QUERY_STRING,
  PAGE_QUERY_STRING,
  SORT_BY_QUERY_STRING,
  SORT_ORDER_QUERY_STRING,
  QUERY_STRING_PRODUCT_IDS,
  QUERY_STRING_PRODUCT_HANDLE,
  EVENT_TRACKING_SORT_COLLECTION,
  EVENT_TRACKING_OPEN_COLLECTION_FILTER,
  EVENT_TRACKING_OPEN_SORT_OPTION,
  MIXPANEL_DESTINATION,
  destinationIntegrations,
  SIB_SEARCH,
  EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE,
  SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX,
} from '@/utils'
import { FilterSection, SortOption } from '@/types'
import { computed } from '@vue/reactivity'
import useUpdateRouter from '@/composables/router'
import Analytics from '@/services/analytics'
import useSettingStore from '@/store/setting'
import EventBus from '@/services/eventbus'

const props = withDefaults(
  defineProps<{
    filterSections: FilterSection[]
    pageType?: string
    collectionHandle?: string
  }>(),
  {
    filterSections: () => [],
    pageType: 'collection',
    collectionHandle: '',
  }
)

const settingStore = useSettingStore()
const route = useRoute()
const hasFilter = ref(false)
const showSort = ref(false)
const showFilter = ref(false)
const isSelectOpen = ref(false)
const attributeBasedVisibility = ref('')
const { updateQueryString } = useUpdateRouter()

const filtersOutAttributeSearchanise = ['price', 'reviews_average_score_titles']
const filtersOutAttributeSibSearch = [
  'price',
  'reviews_average_score_titles',
  'collection_titles',
  'tags',
  'niches',
]

const filtersOutAttribute = computed(() =>
  isSibSearch.value
    ? filtersOutAttributeSibSearch
    : filtersOutAttributeSearchanise
)

const filterAttributes = computed(() => {
  return (
    props.filterSections?.filter(
      (elm) => !filtersOutAttribute.value.includes(elm.attribute)
    ) || []
  )
})

const newFilterVersion = computed(
  () => settingStore.pages?.general?.filterVersion?.toUpperCase() == 'V2'
)

const isSibSearch = computed(
  () => import.meta.env.VITE_SEARCH_TYPE == SIB_SEARCH
)

const collectionIdSortManual = computed(() => {
  const collectionSortManualItem = settingStore.shop?.collectionHandleSortManual
    ?.split(',')
    .find((item) => item.includes(props.collectionHandle))

  if (collectionSortManualItem) {
    return collectionSortManualItem.split('_')[1]
  } else {
    return ''
  }
})

const sortOptions: SortOption[] = [
  {
    sortBy: isSibSearch.value
      ? ''
      : collectionIdSortManual.value != '' && props.pageType == 'search'
      ? `collection_${collectionIdSortManual.value}_position`
      : undefined,
    sortOrder: isSibSearch.value
      ? ''
      : collectionIdSortManual.value != '' && props.pageType == 'search'
      ? 'asc'
      : undefined,
    title: 'Featured',
    label: 'Featured',
    value: 'Featured',
  },
  {
    sortBy: 'created',
    sortOrder: 'desc',
    title: 'Date: New to Old',
    label: 'Date: New to Old',
    value: 'Date: New to Old',
  },
  {
    sortBy: 'created',
    sortOrder: 'asc',
    title: 'Date: Old to New',
    label: 'Date: Old to New',
    value: 'Date: Old to New',
  },
  {
    sortBy: 'price',
    sortOrder: 'asc',
    title: 'Price: Low to High',
    label: 'Price: Low to High',
    value: 'Price: Low to High',
  },
  {
    sortBy: 'price',
    sortOrder: 'desc',
    title: 'Price: High to Low',
    label: 'Price: High to Low',
    value: 'Price: High to Low',
  },
  {
    sortBy: 'sales_amount',
    sortOrder: 'desc',
    title: 'Best selling',
    label: 'Best selling',
    value: 'Best selling',
  },
]

const selectedOptionIndex = computed(() => {
  const sortBy = route.query[SORT_BY_QUERY_STRING]
  const sortOrder = route.query[SORT_ORDER_QUERY_STRING]
  const selectedIndex = sortOptions.findIndex(
    (elm) => elm.sortBy == sortBy && elm.sortOrder == sortOrder
  )
  if (selectedIndex == -1) return 0
  return selectedIndex
})

onMounted(() => {
  const showFilter = route.query['show_filter']
  if (showFilter == 'true') {
    showFilterPopup()
    updateQueryString({
      show_filter: 'false',
    })
  }
})

function getNumberFilterSelected(attribute?: string) {
  let numberFilterSelected = 0

  for (const key in route.query) {
    if (!key.startsWith(SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX)) continue
    if (attribute && key.includes(attribute) && route.query[key]) {
      const number = Array.isArray(route.query[key])
        ? route.query[key]?.length
        : 1
      numberFilterSelected += number || 0
    }
  }
  return numberFilterSelected
}

const sumNumberFilterSelected = computed(() => {
  let numberFilterSelected = 0

  for (const key in route.query) {
    if (!key.startsWith(SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX)) continue
    if (route.query[key]) {
      const number = Array.isArray(route.query[key])
        ? route.query[key]?.length
        : 1
      numberFilterSelected += number || 0
    }
  }
  return numberFilterSelected
})

function onSortSelect(selectedValue: string) {
  const index = sortOptions.findIndex((option) => option.value == selectedValue)
  if (index > -1) {
    const selectedOption = sortOptions[index]
    updateQueryString({
      [SORT_BY_QUERY_STRING]: selectedOption.sortBy,
      [SORT_ORDER_QUERY_STRING]: selectedOption.sortOrder,
      [PAGE_QUERY_STRING]: 1,
      [COLLECTION_AFTER_QUERY_STRING]: undefined,
      [COLLECTION_BEFORE_QUERY_STRING]: undefined,
      [QUERY_STRING_PRODUCT_IDS]: undefined,
      [QUERY_STRING_PRODUCT_HANDLE]: undefined,
    })
  }

  // trackingSortCollection(selectedValue)
}

function handleOpenSortUsingUpDownArrow() {
  if (!showSort.value) {
    // trackingOpenSortOption()
  }
  showSort.value = !showSort.value
  showFilter.value = false
}

function handleOpenSortUsingSelect() {
  if (!isSelectOpen.value) {
    // trackingOpenSortOption()
  }
  isSelectOpen.value = !isSelectOpen.value
}

function trackingOpenSortOption() {
  Analytics.track(
    EVENT_TRACKING_OPEN_SORT_OPTION,
    {
      page: route.name as string,
    },
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
}

function showFilterPopup(attribute?: string) {
  showFilter.value = !showFilter.value
  showSort.value = false

  // trackingOpentCollectionFilter()

  if (newFilterVersion.value) {
    attributeBasedVisibility.value = attribute || ''
    let showFilterQuery = {}
    filterAttributes.value.map((item) => {
      showFilterQuery = {
        ...showFilterQuery,
        [`show_filter_${item.attribute}`]:
          attribute && attribute == item.attribute ? true : undefined,
      }
    })

    updateQueryString(showFilterQuery)
    setTimeout(() => {
      if (attribute) {
        EventBus.trigger(EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE)
      }
    })
  }
}

function trackingSortCollection(selectedValue: string) {
  Analytics.track(
    EVENT_TRACKING_SORT_COLLECTION,
    {
      path: route.path,
      sorted_value: selectedValue,
      collection_type: route.name as string,
    },
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
}

function trackingOpentCollectionFilter() {
  Analytics.track(
    EVENT_TRACKING_OPEN_COLLECTION_FILTER,
    {
      path: route.path,
    },
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
}
</script>

<style lang="scss">
.collection-sort-and-filter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  .option {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 1em;
    position: relative;
    cursor: pointer;

    img {
      width: 1.1em;
      height: 1.1em;
    }
  }

  .sort-option {
    padding-left: 0;
  }
  .filter-option {
    padding-right: 4px;
  }

  .select {
    width: auto;
    margin-left: 1em;
  }

  @include media-md-up {
    justify-content: flex-end;
    margin-bottom: 1em;
  }

  .filter-badge {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--color-primary);
    position: absolute;
    top: 0.8em;
    right: 0px;
  }

  &--new {
    width: 100%;
    display: flex;
    .sib-tag {
      cursor: pointer;
      --tag-md-padding: 0.6rem 1rem;
      --tag-bg: #fff;

      &:hover {
        --tag-hover-bg: var(--tag-default-bg);
      }
    }
    .tag-sort,
    .tag-all-filter {
      overflow: unset;
      display: flex;
      align-items: center;
      line-height: 0;
    }
    .filter-primary {
      path {
        fill: var(--color-primary);
      }
    }
  }
}
</style>
